import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import { Close } from '@mui/icons-material';
import ImageUploader from '../modules/ModuleForm/ImageUploader';

const CreateDashboardCardModal = ({ open, onClose, onSubmit, card: editingCard }) => {
    const [title, setTitle] = useState('');
    const [subTitle, setSubTitle] = useState('');
    const [grid, setGrid] = useState('Grid');
    const [subscription, setSubscription] = useState('Free');
    const [position, setPosition] = useState(0);
    const [imageURL, setImageURL] = useState('');

    const isEditMode = Boolean(editingCard?._id);

    useEffect(() => {
        if (open) {
            if (editingCard) {
                setTitle(editingCard.title ?? '');
                setSubTitle(editingCard.subTitle ?? '');
                setGrid(editingCard.grid === 'Slider' ? 'Slider' : 'Grid');
                setSubscription(['Free', 'Lite', 'Pro'].includes(editingCard.subscription) ? editingCard.subscription : 'Free');
                setPosition(Number.isFinite(Number(editingCard.position)) ? Number(editingCard.position) : 0);
                setImageURL(editingCard.imageURL ?? '');
            } else {
                setTitle('');
                setSubTitle('');
                setGrid('Grid');
                setSubscription('Free');
                setPosition(0);
                setImageURL('');
            }
        }
    }, [open, editingCard]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!title || !title.trim()) return;
        const payload = {
            title: title.trim(),
            subTitle: (subTitle ?? '').trim(),
            grid,
            subscription,
            position: Number.isFinite(Number(position)) ? Number(position) : 0,
            imageURL: imageURL || ''
        };
        if (isEditMode) payload._id = editingCard._id;
        onSubmit(payload);
        setTitle('');
        setSubTitle('');
        setGrid('Grid');
        setSubscription('Free');
        setPosition(0);
        setImageURL('');
        onClose();
    };

    const handleClose = () => {
        setTitle('');
        setSubTitle('');
        setGrid('Grid');
        setSubscription('Free');
        setPosition(0);
        setImageURL('');
        onClose();
    };

    const inputStyle = {
        width: '100%',
        padding: '12px 14px',
        fontSize: '0.95rem',
        border: '1px solid #e0e0e0',
        borderRadius: '10px',
        outline: 'none',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        boxSizing: 'border-box'
    };

    const labelStyle = {
        display: 'block',
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#374151',
        marginBottom: '8px'
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <div
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '100%',
                    maxWidth: '520px',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    background: '#fff',
                    borderRadius: '16px',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.12), 0 12px 24px rgba(0,0,0,0.08)',
                    outline: 'none'
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: '24px 28px 20px',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '16px'
                    }}
                >
                    <h2
                        style={{
                            margin: 0,
                            fontSize: '1.375rem',
                            fontWeight: 700,
                            color: '#1a1a2e',
                            letterSpacing: '-0.02em',
                            lineHeight: 1.3
                        }}
                    >
                        {isEditMode ? 'Edit Dashboard Card' : 'Create Dashboard Card'}
                    </h2>
                    <button
                        type="button"
                        onClick={handleClose}
                        aria-label="Close"
                        style={{
                            padding: '8px',
                            border: 'none',
                            background: '#f5f5f5',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            color: '#666',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.2s ease, color 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.background = '#eee';
                            e.currentTarget.style.color = '#333';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = '#f5f5f5';
                            e.currentTarget.style.color = '#666';
                        }}
                    >
                        <Close style={{ fontSize: '22px' }} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} style={{ padding: '28px 28px 32px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {/* Title */}
                        <div>
                            <label style={labelStyle}>
                                Title <span style={{ color: '#dc3545', fontWeight: 600 }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="e.g. Key Documents"
                                required
                                style={inputStyle}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#4CAF50';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(76, 175, 80, 0.15)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e0e0e0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>

                        {/* Position */}
                        <div>
                            <label style={labelStyle}>Position</label>
                            <input
                                type="number"
                                value={position}
                                onChange={(e) => setPosition(e.target.value)}
                                placeholder="e.g. 1"
                                min="0"
                                style={inputStyle}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#4CAF50';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(76, 175, 80, 0.15)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e0e0e0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>

                        {/* Sub Title */}
                        <div>
                            <label style={labelStyle}>Sub Title</label>
                            <input
                                type="text"
                                value={subTitle}
                                onChange={(e) => setSubTitle(e.target.value)}
                                placeholder="e.g. Upload and manage your files"
                                style={inputStyle}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#4CAF50';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(76, 175, 80, 0.15)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e0e0e0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                        </div>

                        {/* Grid */}
                        <div>
                            <label style={labelStyle}>Layout</label>
                            <select
                                value={grid}
                                onChange={(e) => setGrid(e.target.value)}
                                style={{
                                    ...inputStyle,
                                    cursor: 'pointer',
                                    appearance: 'none',
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 14px center',
                                    paddingRight: '36px'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#4CAF50';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(76, 175, 80, 0.15)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e0e0e0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            >
                                <option value="Grid">Grid</option>
                                <option value="Slider">Slider</option>
                            </select>
                        </div>

                        {/* Subscription */}
                        <div>
                            <label style={labelStyle}>Subscription</label>
                            <select
                                value={subscription}
                                onChange={(e) => setSubscription(e.target.value)}
                                style={{
                                    ...inputStyle,
                                    cursor: 'pointer',
                                    appearance: 'none',
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                                    backgroundRepeat: 'no-repeat',
                                    backgroundPosition: 'right 14px center',
                                    paddingRight: '36px'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#4CAF50';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(76, 175, 80, 0.15)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e0e0e0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            >
                                <option value="Free">Free</option>
                                <option value="Lite">Lite</option>
                                <option value="Pro">Pro</option>
                            </select>
                        </div>

                        {/* Image */}
                        <div style={{ marginTop: '4px' }}>
                            <ImageUploader
                                imageURL={imageURL}
                                onImageChange={setImageURL}
                                label="Image"
                            />
                        </div>
                    </div>

                    {/* Actions */}
                    <div
                        style={{
                            marginTop: '32px',
                            paddingTop: '24px',
                            borderTop: '1px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px'
                        }}
                    >
                        <button
                            type="button"
                            onClick={handleClose}
                            style={{
                                padding: '12px 24px',
                                fontSize: '0.9375rem',
                                fontWeight: 600,
                                color: '#555',
                                background: '#fff',
                                border: '1px solid #ddd',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#f9f9f9';
                                e.currentTarget.style.borderColor = '#ccc';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = '#fff';
                                e.currentTarget.style.borderColor = '#ddd';
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '12px 24px',
                                fontSize: '0.9375rem',
                                fontWeight: 600,
                                color: '#fff',
                                background: '#4CAF50',
                                border: 'none',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(76, 175, 80, 0.35)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#45a049';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(76, 175, 80, 0.4)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = '#4CAF50';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(76, 175, 80, 0.35)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            {isEditMode ? 'Update Card' : 'Create Card'}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default CreateDashboardCardModal;
